import React, { useState, useEffect, useMemo } from 'react';
import { X } from 'lucide-react';
import { useFetch } from '../../hooks/useApi';
import { apiService } from '../../services/apiService';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './dialog';
import ScrollableClubCard from './ScrollableClubCard';
import ScrollableRunCard from './ScrollableRunCard';
import ScrollableMeetingCard from './ScrollableMeetingCard';

// Helper to clean IDs (remove any appended indices like :1)
const cleanId = (id) => {
  if (!id) return null;
  const idStr = String(id);
  // Remove any colon and everything after it (e.g., "id:1" -> "id")
  const cleaned = idStr.split(':')[0].trim();
  return cleaned || null;
};

const ScrollableCardModal = ({ cardType, cardId, isOpen, onClose, onConnectionChange }) => {
  const [cardData, setCardData] = useState(null);
  const [additionalData, setAdditionalData] = useState({});

  // Clean the cardId to remove any appended indices
  const cleanedCardId = useMemo(() => cleanId(cardId), [cardId]);

  // Determine API endpoint based on card type
  const getApiEndpoint = () => {
    switch (cardType) {
      case 'club':
        return () => apiService.clubs.getProfile(cleanedCardId);
      case 'runner':
        return () => apiService.users.getProfile(cleanedCardId);
      case 'run':
      case 'meeting': // 'meeting' is an alias for 'run'
        return () => apiService.meetings.getById(cleanedCardId);
      default:
        throw new Error(`Unsupported card type: ${cardType}`);
    }
  };

  // Fetch card data using useFetch hook
  const { data: response, loading, error } = useFetch(
    getApiEndpoint(),
    [cleanedCardId, cardType],
    { enabled: !!cleanedCardId && isOpen }
  );

  // Update state when data changes
  useEffect(() => {
    if (response) {
      switch (cardType) {
        case 'club':
          setCardData(response.club);
          setAdditionalData({
            upcoming_meetings: response.upcoming_meetings || [],
          });
          break;
        case 'runner':
          setCardData(response);
          setAdditionalData({});
          break;
        case 'run':
        case 'meeting': // 'meeting' is an alias for 'run'
          setCardData(response);
          setAdditionalData({});
          break;
        default:
          break;
      }
    }
  }, [response, cardType]);

  // Handle card data updates (for edit functionality)
  const handleCardUpdate = async (updatedCard) => {
    try {
      const freshResponse = await getApiEndpoint()();
      if (freshResponse) {
        switch (cardType) {
          case 'club':
            setCardData(freshResponse.club);
            setAdditionalData({
              upcoming_meetings: freshResponse.upcoming_meetings || []
            });
            break;
          case 'runner':
          case 'run':
          case 'meeting': // 'meeting' is an alias for 'run'
            setCardData(freshResponse);
            break;
        }
      }
    } catch (error) {
      // Fallback to local update if API fails
      setCardData(updatedCard);
    }
  };

  if (!isOpen) return null;

  // Prepare enhanced card data
  const enhancedCard = cardData ? {
    ...cardData,
    ...additionalData
  } : null;

  // Render appropriate content component
  const renderContent = () => {
    if (!enhancedCard) return null;

    switch (cardType) {
      case 'club':
        return (
          <ScrollableClubCard
            club={enhancedCard}
            isFullProfile={true}
            onClubUpdate={handleCardUpdate}
            onConnectionChange={onConnectionChange}
          />
        );
      case 'runner':
        return (
          <ScrollableRunCard
            runner={enhancedCard}
            isFullProfile={true}
            onRunnerUpdate={handleCardUpdate}
          />
        );
      case 'run':
      case 'meeting': // 'meeting' is an alias for 'run'
        return (
          <ScrollableMeetingCard
            meeting={enhancedCard}
            isFullProfile={true}
            onMeetingUpdate={handleCardUpdate}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-transparent border-none shadow-none max-w-fit p-0 [&>button]:hidden">
        {/* Hidden accessibility elements */}
        <DialogHeader className="sr-only">
          <DialogTitle>{cardType.charAt(0).toUpperCase() + cardType.slice(1)} Card</DialogTitle>
          <DialogDescription>
            {enhancedCard?.name ? `${cardType} card for ${enhancedCard.name}` : `Loading ${cardType} card`}
          </DialogDescription>
        </DialogHeader>
        
        <div className="relative">
          {/* Custom Close Button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 z-10 bg-black/50 hover:bg-black/70 text-white rounded-full p-2 transition-colors duration-200 focus:outline-none"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>

          {/* Loading State */}
          {loading && (
            <div className="w-[400px] max-h-[500px] bg-white/95 backdrop-blur-sm rounded-3xl shadow-2xl border-2 border-black/20 flex items-center justify-center">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto mb-4"></div>
                <p className="text-gray-600 text-sm" style={{fontFamily: 'Montserrat, sans-serif'}}>
                  Loading {cardType}...
                </p>
              </div>
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className="w-[400px] max-h-[500px] bg-white/95 backdrop-blur-sm rounded-3xl shadow-2xl border-2 border-black/20 flex items-center justify-center">
              <div className="text-center p-6">
                <p className="text-red-600 text-sm mb-2" style={{fontFamily: 'Montserrat, sans-serif'}}>
                  Failed to load {cardType}
                </p>
                <p className="text-gray-500 text-xs" style={{fontFamily: 'Montserrat, sans-serif'}}>
                  {error.message || 'Please try again later'}
                </p>
              </div>
            </div>
          )}

          {/* Content */}
          {!loading && !error && renderContent()}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ScrollableCardModal;
